import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { publicAPI } from '../../utils/apiPublic';
import { securityUtils } from '../../utils/apiPrivate';

// Import custom hooks
import { useTroubleshootingForm } from './hooks/useTroubleshootingForm';
import { useNotifications } from './hooks/useNotifications';
import { useCaptcha } from './hooks/useCaptcha';
import { useLocations } from './hooks/useLocations';

// Import components
import PersonalInformationSection from './components/PersonalInformationSection';
import LocationSection from './components/LocationSection';
import EquipmentSection from './components/EquipmentSection';
import MessageAndAttachmentSection from './components/MessageAndAttachmentSection';
import CaptchaSection from './components/CaptchaSection';
import ConfirmationDialog from './components/ConfirmationDialog';
import NotificationSystem from './components/NotificationSystem';

const TroubleshootingForm = () => {
  const navigate = useNavigate();
  const appVersion = import.meta.env.VITE_VERSION;

  // State management
  const [message, setMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);

  // Custom hooks
  const {
    formData,
    setFormData,
    emailError,
    handleChange,
    handleFileChange,
    resetForm,
    getLocationDisplay,
    getEquipmentDisplay,
    formatDate,
    validateEmail
  } = useTroubleshootingForm();

  const {
    showNotification,
    currentNotification,
    addNotification,
    handleNotificationClose
  } = useNotifications();

  const {
    captcha,
    captchaError,
    captchaDisabled,
    setCaptchaError,
    generateCaptcha,
    handleRefreshCaptcha
  } = useCaptcha(addNotification);

  const {
    locations,
    locationsLoading,
    locationsError
  } = useLocations(addNotification);

  // Form submission handlers
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setMessage(null);
    setCaptchaError(null);

    // Validate email first using security utils
    if (!securityUtils.validateEmail(formData.email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    // If CAPTCHA is not shown yet, show it
    if (!showCaptcha) {
      setShowCaptcha(true);
      addNotification('info', 'Please complete the verification code below to continue', 4000);
      return;
    }

    // Validate CAPTCHA input
    if (!formData.captchaCode) {
      setMessage({ type: 'error', text: 'Please enter the verification code' });
      return;
    }

    setShowConfirmation(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmation(false);
    setIsSubmitting(true);
    setMessage(null);
    setCaptchaError(null);

    try {
      const formDataToSend = new FormData();
      
      // Append all form fields with sanitization
      const finalModelOfEquipment = formData.modelOfEquipment === 'Other' ? formData.customModel : formData.modelOfEquipment;

      // Get the correct location name based on type
      let locationName;
      if (formData.locationType === 'SDO') {
        locationName = formData.department;
      } else {
        locationName = formData.schoolName;
      }

      const ticketData = {
        category: 'TROUBLESHOOTING',
        name: securityUtils.sanitizeData(formData.name),
        email: securityUtils.sanitizeData(formData.email),
        department: formData.locationType === 'SDO' ? securityUtils.sanitizeData(formData.department) : null,
        location: securityUtils.sanitizeData(locationName),
        schoolLevel: formData.locationType === 'SCHOOL' ? securityUtils.sanitizeData(formData.schoolLevel) : null,
        schoolName: formData.locationType === 'SCHOOL' ? securityUtils.sanitizeData(formData.schoolName) : null,
        dateOfRequest: formData.dateOfRequest,
        typeOfEquipment: formData.typeOfEquipment === 'Others' ? securityUtils.sanitizeData(formData.customEquipmentType) : formData.typeOfEquipment,
        modelOfEquipment: securityUtils.sanitizeData(finalModelOfEquipment),
        serialNo: securityUtils.sanitizeData(formData.serialNo),
        specificProblem: securityUtils.sanitizeData(formData.specificProblem || ''),
        priority: formData.priority,
        captchaId: captcha.id,
        captchaCode: securityUtils.sanitizeData(formData.captchaCode)
      };

      formDataToSend.append('ticketData', JSON.stringify(ticketData));
      
      // Append file if exists
      if (formData.file) {
        formDataToSend.append('file', formData.file);
      }

      const response = await publicAPI.createTroubleshootingTicket(formDataToSend);

      const { ticketId, trackingId } = response.data;
      
      addNotification('success', 
        `Ticket #${ticketId} created successfully! Tracking ID: ${trackingId}`, 
        10000
      );

      // Reset form
      resetForm();
      setShowCaptcha(false);
      generateCaptcha();

    } catch (error) {
      console.error('Error submitting ticket:', error);
      const errorMessage = error.response?.data?.message || 'Unable to submit request';
      
      if (error.response?.status === 429) {
        const rateLimitInfo = error.response.data.rateLimitInfo;
        addNotification('error', 
          `Rate limit exceeded. Please wait ${rateLimitInfo.cooldownMinutes} minutes. ` +
          `Remaining attempts: ${rateLimitInfo.remainingAttempts}`, 
          15000
        );
      } else if (error.response?.status === 400 && error.response.data?.captchaError) {
        setCaptchaError(error.response.data.message);
        addNotification('warning', 
          `Invalid CAPTCHA code. Please try again. ${error.response.data.remainingAttempts} attempts remaining.`, 
          8000
        );
        generateCaptcha();
      } else {
        addNotification('error', `Error: ${errorMessage}`, 10000);
        generateCaptcha();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col relative">
      {/* Background Image */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat opacity-30 z-0"
        style={{ backgroundImage: 'url("/background.jpg")' }}
      />


      {/* Header */}
      <div className="w-full bg-white/95 backdrop-blur-md border-b border-gray-300 text-black z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center">
            <h1 
              className="text-2xl sm:text-3xl font-bold text-black font-poppins mb-2 hover:text-blue-600 transition-colors duration-300 cursor-pointer"
              onClick={() => navigate('/tickets')}
            >
              Troubleshooting Request
            </h1>
            <p className="text-sm sm:text-base text-gray-600 font-poppins">
              Please fill out the form below to submit your troubleshooting request
            </p>
          </div>
        </div>
      </div>

      {/* Main Form */}
      <div className="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 relative overflow-hidden">
          {/* Loading Overlay */}
          {isSubmitting && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center rounded-2xl z-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-700 font-poppins text-lg">
                Submitting your request...
              </p>
            </div>
          )}

          {/* Error Messages */}
          {message && (
            <div className={`p-4 mx-6 mt-6 rounded-lg border ${
              message.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' :
              message.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' :
              'bg-blue-50 border-blue-200 text-blue-700'
            }`}>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  {message.type === 'error' ? (
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  ) : message.type === 'warning' ? (
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.56 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  ) : (
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  )}
                </svg>
                <p className="font-poppins">{message.text}</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 sm:p-8">
            {/* Top Row - Personal Info and Location */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <PersonalInformationSection
                formData={formData}
                handleChange={handleChange}
                emailError={emailError}
                isSubmitting={isSubmitting}
              />

              <LocationSection
                formData={formData}
                handleChange={handleChange}
                locations={locations}
                locationsLoading={locationsLoading}
                locationsError={locationsError}
                isSubmitting={isSubmitting}
              />
            </div>

            {/* Equipment Section */}
            <div className="mb-6">
              <EquipmentSection
                formData={formData}
                handleChange={handleChange}
                isSubmitting={isSubmitting}
              />
            </div>

            {/* Message and Attachment Section */}
            <div className="mb-6">
              <MessageAndAttachmentSection
                formData={formData}
                handleChange={handleChange}
                handleFileChange={handleFileChange}
                addNotification={addNotification}
                isSubmitting={isSubmitting}
              />
            </div>

            {/* CAPTCHA Section - Only show when user clicks submit */}
            {showCaptcha && (
              <div className="mb-8">
                <CaptchaSection
                  formData={formData}
                  setFormData={setFormData}
                  captcha={captcha}
                  captchaError={captchaError}
                  captchaDisabled={captchaDisabled}
                  handleRefreshCaptcha={handleRefreshCaptcha}
                  isSubmitting={isSubmitting}
                />
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-4 bg-blue-600 text-white rounded-xl font-semibold font-poppins text-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    Submitting Request...
                  </>
                ) : showCaptcha ? (
                  'Submit Request'
                ) : (
                  'Submit Request'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        formData={formData}
        getLocationDisplay={getLocationDisplay}
        getEquipmentDisplay={getEquipmentDisplay}
        formatDate={formatDate}
        onConfirm={handleConfirmSubmit}
        isSubmitting={isSubmitting}
      />

      {/* Notification System */}
      <NotificationSystem
        showNotification={showNotification}
        currentNotification={currentNotification}
        handleNotificationClose={handleNotificationClose}
      />

      {/* Version Footer */}
      <div className="w-full text-center py-4 mt-auto">
        <p className="text-xs sm:text-sm text-gray-400 font-poppins italic">
          version {appVersion}
        </p>
      </div>
    </div>
  );
};

export default TroubleshootingForm;